/**
 * Common form state sub-hook.
 * Manages: title, merchantGroup, fundingAccount, asins, dates.
 * Includes initialization from config and initialData population.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { SelectProps } from '@amzn/awsui-components-core/select';
import { CampaignType } from 'src/@types/valueMatching/enums';
import { CampaignConfigurationData } from 'src/@types/valueMatching/campaignConfiguration';
import { FormMode } from 'src/components/organisms/CampaignForm/types';
import { CampaignData } from 'src/@types/valueMatching/campaign';
import { epochToLocalParts } from 'src/utils/commonUtils';
import { getDefaultMerchantGroup, getCpgnAccountsForMerchant } from './helpers';

export interface UseCommonFormStateParams {
    /** Campaign configuration fetched from the API; null while loading or on error. */
    config: CampaignConfigurationData | null;
    /**
     * The resolved campaign type (e.g. NCEMI, CASHBACK).
     * `undefined` while the slug is being resolved — the hook skips initialisation until it is defined.
     */
    campaignType: CampaignType | undefined;
    /** Whether the merchant group selector is visible (true for NCEMI/DEAL campaigns). */
    showMerchantGroup: boolean;
    /** Current form mode — determines whether fields are editable and how initialisation works. */
    mode: FormMode;
    /** Pre-existing campaign data used to populate fields in modify/view mode. Ignored in create mode. */
    initialData?: CampaignData;
}

export function useCommonFormState({
    config,
    campaignType,
    showMerchantGroup,
    mode,
    initialData,
}: UseCommonFormStateParams) {
    const [title, setTitle] = useState('');
    const [selectedMerchantGroup, setSelectedMerchantGroup] = useState('');
    const [selectedFundingAccount, setSelectedFundingAccount] = useState('');
    const [asins, setAsins] = useState('');
    const [suspendedAsins, setSuspendedAsins] = useState('');
    const [startDate, setStartDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endDate, setEndDate] = useState('');
    const [endTime, setEndTime] = useState('');

    // ── Initialise merchant group + funding account from config ─────────
    // Runs on create mode only (or modify without initialData as a fallback).
    // `showMerchantGroup` is a dependency because the funding-account source
    // changes depending on whether the merchant-group selector is shown.
    useEffect(() => {
        // In modify/view mode with initialData present, skip config-based init entirely
        // to preserve values populated by the initialData effect.
        if (mode !== FormMode.CREATE && initialData) return;

        if (!config || !campaignType) {
            // Reset to avoid stale state from a previous campaign type
            setSelectedMerchantGroup('');
            setSelectedFundingAccount('');
            return;
        }

        if (showMerchantGroup && config.merchantCPGNDetails.length > 0) {
            const defaultGroup = getDefaultMerchantGroup(config.merchantCPGNDetails);
            setSelectedMerchantGroup(defaultGroup);

            const accounts = getCpgnAccountsForMerchant(config.merchantCPGNDetails, defaultGroup);
            setSelectedFundingAccount(accounts.length > 0 ? accounts[0] : '');
        } else {
            // Reset merchant group when not shown, to avoid stale state
            setSelectedMerchantGroup('');
            setSelectedFundingAccount(
                config.aggregatedCPGNAccounts.length > 0 ? config.aggregatedCPGNAccounts[0] : '',
            );
        }
    }, [config, campaignType, showMerchantGroup, mode, initialData]);

    // ── Populate common fields from initialData (modify / view) ─────────
    useEffect(() => {
        if (mode === FormMode.CREATE || !initialData) return;

        setTitle(initialData.title ?? '');
        setAsins(initialData.includedProducts?.asins?.join(', ') ?? '');
        setSuspendedAsins(initialData.suspendedProducts?.asins?.join(', ') ?? '');
        setSelectedMerchantGroup(initialData.merchantDetail?.merchantGroup ?? '');
        setSelectedFundingAccount(initialData.cpgnAccount ?? '');

        if (initialData.startTime) {
            try {
                // Convert epoch → marketplace-local date/time using Intl.DateTimeFormat.
                // localPartsToEpoch (in formDataBuilder) converts back to epoch — roundtrip is lossless.
                const { date, time } = epochToLocalParts(initialData.startTime);
                setStartDate(date);
                setStartTime(time);
            } catch {
                // leave fields empty if date is unparseable
            }
        }
        if (initialData.endTime) {
            try {
                const { date, time } = epochToLocalParts(initialData.endTime);
                setEndDate(date);
                setEndTime(time);
            } catch {
                // leave fields empty if date is unparseable
            }
        }
    }, [mode, initialData]);

    // ── Merchant group options (NCEMI/DEAL) ─────────────────────────────
    const merchantGroupOptions: SelectProps.Option[] = useMemo(() => {
        if (!config) {
            // In view/modify mode without config, derive a single option from the selected value
            // so that the Select component can display it.
            if (selectedMerchantGroup) {
                return [{ label: selectedMerchantGroup, value: selectedMerchantGroup }];
            }
            return [];
        }
        return config.merchantCPGNDetails.map((merchant) => ({
            label: merchant.merchantGroup,
            value: merchant.merchantGroup,
        }));
    }, [config, selectedMerchantGroup]);

    // ── Funding account options ─────────────────────────────────────────
    const fundingAccountOptions: SelectProps.Option[] = useMemo(() => {
        if (!config) {
            // In view/modify mode without config, derive a single option from the selected value
            // so that the Select component can display it.
            if (selectedFundingAccount) {
                return [{ label: selectedFundingAccount, value: selectedFundingAccount }];
            }
            return [];
        }

        if (showMerchantGroup) {
            const accounts = getCpgnAccountsForMerchant(
                config.merchantCPGNDetails,
                selectedMerchantGroup,
            );
            return accounts.map((account) => ({ label: account, value: account }));
        }

        return config.aggregatedCPGNAccounts.map((account) => ({ label: account, value: account }));
    }, [config, showMerchantGroup, selectedMerchantGroup, selectedFundingAccount]);

    // ── Merchant group change handler ───────────────────────────────────
    const handleMerchantGroupChange = useCallback((value: string) => {
        setSelectedMerchantGroup(value);
        if (config) {
            const accounts = getCpgnAccountsForMerchant(config.merchantCPGNDetails, value);
            setSelectedFundingAccount(accounts.length > 0 ? accounts[0] : '');
        }
    }, [config]);

    return {
        title,
        setTitle,
        selectedMerchantGroup,
        setSelectedMerchantGroup,
        selectedFundingAccount,
        setSelectedFundingAccount,
        asins,
        setAsins,
        suspendedAsins,
        setSuspendedAsins,
        startDate,
        setStartDate,
        startTime,
        setStartTime,
        endDate,
        setEndDate,
        endTime,
        setEndTime,
        merchantGroupOptions,
        fundingAccountOptions,
        handleMerchantGroupChange,
    };
}
